import { ServicesManager } from '@ohif/core';
import { ToolButton } from '@ohif/ui-next';
import React, { PropsWithChildren, useEffect, useState } from 'react';

type CompositeToolbarButtonProps = PropsWithChildren<{
  id: string;
  servicesManager: ServicesManager;
  /**
   * The hanging-protocol id this button toggles. The button is "active" while
   * that protocol is applied. Passed explicitly so this component stays generic
   * instead of hardcoding a specific protocol (e.g. MIP).
   */
  activeProtocolId?: string;
}> &
  React.ComponentProps<typeof ToolButton>;

export function CompositeToolbarButton({
  children,
  id,
  servicesManager,
  activeProtocolId,
  ...props
}: CompositeToolbarButtonProps) {
  const { hangingProtocolService } = servicesManager?.services || {};

  // Active state is driven by the hanging protocol, NOT by
  // `toolbarService.state.toggles`. OHIF 3.10+ removed that toggle-tracking
  // (button active state moved to `evaluate` functions), so the previous
  // `buttonsState.toggles[id]` read was permanently false — the button never
  // showed as active. Follow the protocol the button applies instead.
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!hangingProtocolService || !activeProtocolId) {
      return;
    }
    const update = () =>
      setIsActive(
        hangingProtocolService.getState().protocolId === activeProtocolId
      );
    update();
    const { unsubscribe } = hangingProtocolService.subscribe(
      hangingProtocolService.EVENTS.PROTOCOL_CHANGED,
      update
    );
    return () => unsubscribe();
  }, [hangingProtocolService, activeProtocolId]);

  return (
    <div key={id} className="flex items-center text-white">
      {/* OHIF 3.11 deleted @ohif/ui's ToolbarButton; ui-next's ToolButton is the
          equivalent (it is what upstream's own toolbar renders). It takes the
          tooltip text from `tooltip` rather than falling back to `label`, so
          keep the old behaviour of labelling the button on hover. */}
      <ToolButton
        {...props}
        id={id}
        tooltip={props.tooltip ?? props.label}
        isActive={isActive}
      />
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return null;
        }
        // `isActive`/`servicesManager` must come AFTER `...props` so the values
        // computed here win — `componentProps` from the toolbar may carry its own
        // (stale/undefined) `isActive`, and spreading it last would clobber ours.
        return React.cloneElement(child, {
          ...props,
          servicesManager,
          isActive,
        });
      })}
    </div>
  );
}
